import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert, Table } from 'react-bootstrap';
import { get, post } from '../../../helpers/httpClient';

function ClientsCatalog() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [impersonatingId, setImpersonatingId] = useState(null);

  const loadClients = () => {
    setLoading(true);
    setError('');
    get('admin/users', (resp) => {
      setClients(resp?.users || resp || []);
    })
      .catch(() => setError('Failed to load clients.'))
      .finally(() => setLoading(false));
  };

  const createClient = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Client name is required');
      return;
    }
    setCreating(true);
    setError('');
    post('admin/users', form, () => {
      loadClients();
      setForm({ name: '', email: '', password: '' });
    })
      .catch(() => setError('Failed to create client.'))
      .finally(() => setCreating(false));
  };

  const impersonate = async (user) => {
    if (!user?.id) return;
    try {
      setImpersonatingId(user.id);
      const resp = await post(`admin/users/${user.id}/impersonate`, {});
      // Expecting token in response, either directly or nested as resp.token
      const token = resp?.token || resp?.data?.token || resp;
      if (typeof token === 'string' && token.length > 0) {
        localStorage.setItem('token', token);
        // reload app context to reflect new identity
        window.location.href = '/';
      } else {
        throw new Error('Token not found in response');
      }
    } catch (e) {
      setError('Failed to impersonate user.');
    } finally {
      setImpersonatingId(null);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  return (
    <Container className="mt-4">
      <Row className="mb-3">
        <Col>
          <h3>Clients</h3>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={8} lg={6}>
          <Card>
            <Card.Body>
              <Card.Title>Create Client</Card.Title>
              {error && <Alert variant="danger">{error}</Alert>}
              <Form onSubmit={createClient}>
                <Form.Group className="mb-3 form-row-horizontal" controlId="clientName">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="John Doe"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="mb-3 form-row-horizontal" controlId="clientEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="mb-3 form-row-horizontal" controlId="clientPassword">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </Form.Group>
                <div className="d-flex justify-content-end">
                  <Button type="submit" variant="primary" disabled={creating} className="login-button">
                    {creating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" /> Creating...
                      </>
                    ) : (
                      'Create'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          {loading ? (
            <div className="text-center my-4">
              <Spinner animation="border" />
            </div>
          ) : clients.length === 0 ? (
            <Alert variant="info">No clients yet. Create your first client.</Alert>
          ) : (
            <div className="clients-table-wrapper">
              <Table responsive bordered hover className="clients-table align-middle">
                <thead>
                  <tr>
                    <th className="col-narrow">#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th className="col-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c, idx) => (
                    <tr key={c.id || idx}>
                      <td>{idx + 1}</td>
                      <td>{c.name || 'Unnamed Client'}</td>
                      <td>{c.email || '-'}</td>
                      <td>
                        <Button
                          size="sm"
                          className="login-button"
                          disabled={!c.id || impersonatingId === c.id}
                          onClick={() => impersonate(c)}
                          variant="primary"
                        >
                          {impersonatingId === c.id ? (
                            <><Spinner animation="border" size="sm" className="me-2" /> Impersonating...</>
                          ) : (
                            'Impersonate'
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default ClientsCatalog;
